/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Button, IconButton, Radio, Slider, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { ConfigSection } from '../../../common';
import Switch from '../../../common/Switch';
import { useMerchant } from '../../../contexts/MerchantContext';
import { useUploadRoomSceneImageMutation } from '../../../store/modules/merchant/api';
import type {
  AgenticRoomScene,
  AgenticRoomSceneImage,
  AgenticRoomScenes,
} from '../../../store/modules/merchant/types';
import { toast } from '../../../util/toast';
import { useEllaDraft } from '../EllaDraftContext';
import { FORM_MAX_WIDTH, radioSx } from '../ellaFormStyles';
import {
  EMPTY_ROOM_SCENES,
  MAX_ROOM_SCENES,
  MAX_SCENE_NAME_LENGTH,
  MAX_SCENE_WHEN_TO_USE_LENGTH,
  ROOM_SCENE_IMAGE_TYPES,
  addRoomScene,
  removeRoomScene,
  roomSceneImageError,
  updateRoomScene,
} from '../roomScenes';

const SceneImageInput = ({
  label,
  focusLabel,
  hint,
  image,
  disabled,
  onChange,
  onRemove,
}: {
  label: string;
  /** Names this image's focus sliders for screen readers. */
  focusLabel: string;
  hint: string;
  image: AgenticRoomSceneImage | undefined;
  disabled: boolean;
  onChange: (image: AgenticRoomSceneImage) => void;
  onRemove?: () => void;
}) => {
  const { merchantId } = useMerchant();
  const [upload, { isLoading }] = useUploadRoomSceneImageMutation();

  const onFile = async (file: File | undefined) => {
    if (!file || !merchantId) {
      return;
    }
    const problem = roomSceneImageError(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      const { imageSrc } = await upload({ merchantId, file }).unwrap();
      // A new photo starts centered: the old focal point was for the old photo.
      onChange({ src: imageSrc });
    } catch {
      toast.error('Failed to upload image');
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    // Checked in onFile instead, so a wrong file gets a message rather than silently doing nothing.
    multiple: false,
    disabled: disabled || isLoading,
    onDrop: (files) => {
      void onFile(files[0]);
    },
  });

  const focalPoint = image?.focalPoint;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 200 }}>
      <Typography variant="small" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Box
        {...getRootProps()}
        sx={{
          height: 140,
          borderRadius: 2,
          border: image ? 'none' : '1px dashed',
          borderColor: 'grey.300',
          backgroundColor: 'grey.100',
          backgroundImage: image ? `url(${JSON.stringify(image.src)})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: `${focalPoint?.x ?? 50}% ${focalPoint?.y ?? 50}%`,
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 1,
        }}
      >
        <input {...getInputProps()} accept={ROOM_SCENE_IMAGE_TYPES.join(',')} aria-label={label} />
        {!image && (
          <Typography variant="small" color="text.secondary">
            {isLoading ? 'Uploading...' : 'Drop an image here or click to choose'}
          </Typography>
        )}
      </Box>
      <Typography variant="small" color="text.secondary">
        {isLoading && image
          ? 'Uploading...'
          : `${hint}${image ? ' Click the image to replace it.' : ''}`}
      </Typography>
      {image && onRemove && !disabled && (
        <Box>
          <Button size="small" onClick={onRemove} sx={{ textTransform: 'none', px: 0 }}>
            Remove
          </Button>
        </Box>
      )}
      {image &&
        (['x', 'y'] as const).map((axis) => (
          <Box key={axis}>
            <Typography variant="small" color="text.secondary">
              {axis === 'x' ? 'Keep in view: left to right' : 'Keep in view: top to bottom'}
            </Typography>
            <Slider
              size="small"
              min={0}
              max={100}
              value={focalPoint?.[axis] ?? 50}
              disabled={disabled}
              onChange={(_e, value) =>
                onChange({
                  ...image,
                  focalPoint: {
                    x: focalPoint?.x ?? 50,
                    y: focalPoint?.y ?? 50,
                    [axis]: value as number,
                  },
                })
              }
              aria-label={`${focusLabel} ${axis === 'x' ? 'horizontal' : 'vertical'} focus`}
            />
          </Box>
        ))}
    </Box>
  );
};

const SceneCard = ({
  scene,
  position,
  isDefault,
  disabled,
  onChange,
  onMakeDefault,
  onRemove,
}: {
  scene: AgenticRoomScene;
  position: number;
  isDefault: boolean;
  disabled: boolean;
  onChange: (change: Partial<Omit<AgenticRoomScene, 'id'>>) => void;
  onMakeDefault: () => void;
  onRemove: () => void;
}) => {
  return (
    <Box
      data-testid={`room-scene-${position}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <TextField
          fullWidth
          label="Name"
          placeholder="Rainy day"
          value={scene.name}
          disabled={disabled}
          onChange={(e) => onChange({ name: e.target.value })}
          inputProps={{
            maxLength: MAX_SCENE_NAME_LENGTH,
            'aria-label': `Scene ${position} name`,
          }}
        />
        <IconButton
          aria-label={`Remove scene ${position}`}
          onClick={onRemove}
          disabled={disabled}
          sx={{ mt: 0.5 }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={2}
        label="When to use"
        placeholder="The shopper mentions rain or wet weather"
        helperText={`Your agent reads this to decide when the scene fits. ${scene.whenToUse.length}/${MAX_SCENE_WHEN_TO_USE_LENGTH}`}
        FormHelperTextProps={{ sx: { mx: 0 } }}
        value={scene.whenToUse}
        disabled={disabled}
        onChange={(e) => onChange({ whenToUse: e.target.value })}
        inputProps={{
          maxLength: MAX_SCENE_WHEN_TO_USE_LENGTH,
          'aria-label': `Scene ${position} when to use`,
        }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <SceneImageInput
          label={`Scene ${position} image`}
          focusLabel={`Scene ${position}`}
          hint="Wide, calm photos work best. JPEG or PNG, up to 10 MB."
          image={scene.image.src ? scene.image : undefined}
          disabled={disabled}
          onChange={(image) => onChange({ image })}
        />
        <SceneImageInput
          label={`Scene ${position} phone image (optional)`}
          focusLabel={`Scene ${position} phone`}
          hint="A tall crop for phones held upright. Otherwise the main image is used."
          image={scene.imagePhone}
          disabled={disabled}
          onChange={(imagePhone) => onChange({ imagePhone })}
          onRemove={() => onChange({ imagePhone: undefined })}
        />
      </Box>
      <Box
        component="label"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <Radio
          size="small"
          checked={isDefault}
          disabled={disabled}
          onChange={onMakeDefault}
          sx={radioSx}
          inputProps={{ 'aria-label': `Use scene ${position} as the default` }}
        />
        <Typography variant="small">
          Default: the room opens in this scene until the conversation calls for another
        </Typography>
      </Box>
    </Box>
  );
};

/** The merchant's backdrops for the full-page view, which the agent chooses between. */
export const RoomScenesSection = (): JSX.Element => {
  const { draft, readOnly, updateDraft } = useEllaDraft();
  const roomScenes = draft.productExperience.roomScenes ?? EMPTY_ROOM_SCENES;
  const { scenes } = roomScenes;

  const update = (change: (current: AgenticRoomScenes) => AgenticRoomScenes) =>
    updateDraft((prev) => ({
      ...prev,
      productExperience: {
        ...prev.productExperience,
        roomScenes: change(prev.productExperience.roomScenes ?? EMPTY_ROOM_SCENES),
      },
    }));

  return (
    <ConfigSection
      title="Background scenes"
      description="Add photo backgrounds to the full-page view. Your agent picks the one that fits the conversation."
      marginBottom={2}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: FORM_MAX_WIDTH }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Switch
            isChecked={roomScenes.enabled}
            isDisabled={readOnly || scenes.length === 0}
            onToggle={(enabled) => update((current) => ({ ...current, enabled }))}
            aria-label="Use background scenes"
            data-testid="room-scenes-switch"
          />
          <Typography variant="medium">
            {scenes.length === 0
              ? 'Add a scene to turn this on.'
              : `Background scenes are ${roomScenes.enabled ? 'on' : 'off'}.`}
          </Typography>
        </Box>
        {scenes.map((scene, index) => (
          <SceneCard
            key={scene.id}
            scene={scene}
            position={index + 1}
            isDefault={roomScenes.defaultSceneId === scene.id}
            disabled={readOnly}
            onChange={(change) => update((current) => updateRoomScene(current, scene.id, change))}
            onMakeDefault={() => update((current) => ({ ...current, defaultSceneId: scene.id }))}
            onRemove={() => update((current) => removeRoomScene(current, scene.id))}
          />
        ))}
        <Box>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            disabled={readOnly || scenes.length >= MAX_ROOM_SCENES}
            onClick={() => update(addRoomScene)}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              px: 2.5,
              color: 'text.primary',
              borderColor: 'divider',
            }}
          >
            Add scene
          </Button>
        </Box>
      </Box>
    </ConfigSection>
  );
};
